import React, { useState, useEffect, useCallback } from 'react';
import Header from '../Header/Header';
import Footer from '../Header/Footer';
import Config from '../Header/Config';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';

export default function PurchasePayable({ runningBalance }) {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [supplierName, setSupplierName] = useState('');
  const [mobile_no, setMobileNo] = useState('');
  const [address, setAddress] = useState('');
  const [pendingAmount, setPendingAmount] = useState('');
  const [paidAmount, setPaidAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [tax, setTax] = useState('');
  const [availableBalance, setAvailableBalance] = useState('');
  const apiUrl = Config.apiUrl;
  const [isDataChanged, setIsDataChanged] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDataChanged) {
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDataChanged]);

  const handleHeaderNavigation = () => {
    if (isDataChanged && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      return false;
    }
    return true;
  };

  const AddPurchasePayable = async () => {
    if (!date || !supplierName || !mobile_no || !address || !paidAmount || !tax || !paymentMode) {
      toast.warning('Please fill in all required fields');
      return;
    }

    let item = {
      date: date,
      supplier_name: supplierName,
      mobile_no: mobile_no,
      address: address,
      pending_amount: pendingAmount,
      paid_amount: paidAmount,
      available_balance: Number(pendingAmount) - Number(paidAmount),
      payment_mode: paymentMode,
      trx_no: tax,
    };

    try {
      let result = await fetch(`${apiUrl}/addPurchasePayable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(item),
      });

      if (result.ok) {
        toast.success('Purchase payable added successfully');
        // Reset form fields after successful submission
        setIsDataChanged(false);
        setDate(today);
        setSupplierName('');
        setMobileNo('');
        setAddress('');
        setPendingAmount('');
        setPaidAmount('');
        setTax('');
        setPaymentMode('');
        setAvailableBalance('');
      } else {
        toast.error('Failed to add Purchase payable');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to add Purchase payable');
    }
  };

  const cancelFields = () => {
    if (isDataChanged && !window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
      return;
    }
    setDate(today);
    setSupplierName('');
    setMobileNo('');
    setAddress('');
    setPendingAmount('');
    setPaidAmount('');
    setTax('');
    setPaymentMode('');
    setAvailableBalance('');
    setIsDataChanged(false);
  };

  const handleInputChange = (e) => {
    setSupplierName(e.target.value);
    setIsDataChanged(true);
  };

  const [supplierList, setSupplierList] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/get_all_supplier`);
        const data = await response.json();
        setSupplierList(data.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const handleSupplierChange = (selectedSupplier) => {
    const selectedSupplierInfo = supplierList.find((supplier) => supplier.supplier_name === selectedSupplier);

    if (selectedSupplierInfo) {
      setMobileNo(selectedSupplierInfo.contact || '');
      setAddress(selectedSupplierInfo.address || '');
    } else {
      setMobileNo('');
      setAddress('');
    }

    setSupplierName(selectedSupplier);
    setIsDataChanged(true);
  };

  const fetchPendingAmount = useCallback(async () => {
    if (!mobile_no) {
      setPendingAmount('');
      setAvailableBalance('');
      return;
    }

    try {
      const response = await axios.get(`${apiUrl}/getPurchasePayablePendingAmount/${mobile_no}`);
      const resp = response.data;

      const extractNumber = (obj) => {
        if (obj == null) return null;
        if (typeof obj === 'number') return obj;
        if (typeof obj === 'string') {
          const cleaned = obj.replace(/,/g, '');
          const n = Number(cleaned);
          return isNaN(n) ? null : n;
        }
        if (Array.isArray(obj)) {
          for (const item of obj) {
            const n = extractNumber(item);
            if (n !== null) return n;
          }
          return null;
        }
        if (typeof obj === 'object') {
          // first look for keys with relevant names
          for (const key of Object.keys(obj)) {
            if (/(pending|due|amount|balance)/i.test(key)) {
              const n = extractNumber(obj[key]);
              if (n !== null) return n;
            }
          }
          // fallback: deep search any child
          for (const key of Object.keys(obj)) {
            const n = extractNumber(obj[key]);
            if (n !== null) return n;
          }
        }
        return null;
      };

      let pendingAmountValue = extractNumber(resp);
      if (pendingAmountValue === null) pendingAmountValue = 0;
      setPendingAmount(pendingAmountValue);
    } catch (error) {
      console.error('API Error:', error);
      setPendingAmount('');
      setAvailableBalance('');
    }
  }, [mobile_no, apiUrl]);

  const handleMobileNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobileNo(value);
    setIsDataChanged(true);
  };

  useEffect(() => {
    fetchPendingAmount();
  }, [mobile_no, fetchPendingAmount]);

  // recalc available balance from pending and paid
  useEffect(() => {
    const p = Number(pendingAmount) || 0;
    const paid = Number(paidAmount) || 0;
    setAvailableBalance(p - paid);
  }, [pendingAmount, paidAmount]);

  return (
    <>
      <ToastContainer autoClose="1500" />
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header onNavigate={handleHeaderNavigation} />
          <div className="layout-page">
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                <h4 className="fw-bold py-3 mb-4">Purchase Payable</h4>
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 heading-heighlight">Supplier Details</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-company">
                          Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="basic-default-date"
                          placeholder="Enter date"
                          value={date}
                          onChange={(e) => setDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-email">
                          Supplier Name
                        </label>
                        <div className="input-group input-group-merge">
                          <select
                            className="form-control"
                            id="basic-default-email"
                            aria-label="john.doe"
                            aria-describedby="basic-default-email2"
                            value={supplierName}
                            onChange={(e) => handleSupplierChange(e.target.value)}
                          >
                            <option value="" disabled>
                              Select Supplier
                            </option>
                            {supplierList.map((supplier, index) => (
                              <option key={index} value={supplier.supplier_name}>
                                {supplier.supplier_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-phone">
                          Mobile Number
                        </label>
                        <input
                          type="text"
                          id="basic-default-phone"
                          className="form-control phone-mask"
                          placeholder="Enter Mobile Number"
                          value={mobile_no}
                          onChange={handleMobileNumberChange}
                          maxLength={10}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-phone">
                          Address
                        </label>
                        <textarea
                          id="basic-default-message"
                          className="form-control"
                          placeholder="Enter Address"
                          defaultValue={''}
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          rows={1}
                        />
                      </div>
                      <hr className="mb-3 mt-3" />
                      <h5 className="mb-4 heading-heighlight">Billing Details</h5>
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-phone">
                          Pending Amount
                        </label>
                        <input
                          type="text"
                          id="pendingAmountField"
                          className="form-control phone-mask"
                          placeholder="Enter Pending Amount"
                          value={pendingAmount}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d.,\s]/g, '');
                            setPendingAmount(value);
                            setIsDataChanged(true);
                          }}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-phone">
                          Paid Amount
                        </label>
                        <input
                          type="text"
                          id="basic-default-phone"
                          className="form-control phone-mask"
                          placeholder="Enter Paid Amount"
                          value={paidAmount}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d.,\s]/g, '');
                            setPaidAmount(value);
                            setIsDataChanged(true);
                          }}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-phone">
                          Available Balance
                        </label>
                        <input
                          type="text"
                          id="basic-default-phone"
                          className="form-control phone-mask"
                          placeholder="Enter Available Balance"
                          value={Number(pendingAmount) - Number(paidAmount)}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d.,\s]/g, '');
                            setAvailableBalance(value);
                            setIsDataChanged(true);
                          }}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-phone">
                          Payment Mode
                        </label>
                        <select
                          className="form-select"
                          id="basic-default-company"
                          defaultValue=""
                          value={paymentMode}
                          onChange={(e) => setPaymentMode(e.target.value)}
                        >
                          <option value="" disabled>
                            Select Payment Mode
                          </option>
                          <option value="Net Banking">Net Banking</option>
                          <option value="Cash Deposit">Cash Deposit</option>
                          <option value="Paytm">Paytm</option>
                          <option value="Phone Pay">Phone Pay</option>
                          <option value="Google Pay">Google Pay</option>
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label" htmlFor="basic-default-phone">
                          Tax No
                        </label>
                        <input
                          type="text"
                          id="basic-default-phone"
                          className="form-control phone-mask"
                          placeholder="Enter Tax No"
                          value={tax}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setTax(value);
                            setIsDataChanged(true);
                          }}
                        />
                      </div>
                      <div className="col-12 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ marginLeft: '5px', marginRight: '5px' }}
                          onClick={AddPurchasePayable}
                        >
                          Submit
                        </button>
                        <button type="button" className="btn btn-danger" onClick={cancelFields}>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Footer />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
